import { createApp } from 'vue';
import AiFloatingHandle from '@/components/AiFloatingHandle.vue';
import type { SunonAiHandleConfig } from '@/types/ai-handle';

const config: SunonAiHandleConfig = window._AI_CONFIG?.handle ?? {};
const HANDLE_SELECTOR = '.ai-open-handle-btn';

const findTarget = (selector: string): HTMLElement | null => {
  try {
    return document.querySelector<HTMLElement>(selector);
  } catch (error) {
    console.error('[AI Handle] selector 格式无效。', error);
    return null;
  }
};

const mountHandle = (target: HTMLElement, handleConfig: SunonAiHandleConfig) => {
  if (target.dataset.sunonAiHandleMounted === 'true') return;

  target.dataset.sunonAiHandleMounted = 'true';
  target.style.opacity = '1';
  const syncHandleSize = () => {
    const height = target.getBoundingClientRect().height;
    if (height > 0) {
      target.style.setProperty('--sunon-ai-handle-size', `${height}px`);
    }
  };

  syncHandleSize();
  new ResizeObserver(syncHandleSize).observe(target);
  createApp(AiFloatingHandle, { ...handleConfig }).mount(target);
};

const initialize = (handleConfig: SunonAiHandleConfig) => {
  const target = findTarget(HANDLE_SELECTOR);
  if (target) {
    mountHandle(target, handleConfig);
    return;
  }

  const timeout = Math.max(0, handleConfig.waitTimeout ?? 10000);
  if (timeout === 0) {
    console.warn(`[AI Handle] 未找到挂载元素：${HANDLE_SELECTOR}`);
    return;
  }

  let timer = 0;
  const observer = new MutationObserver(() => {
    const asyncTarget = findTarget(HANDLE_SELECTOR);
    if (!asyncTarget) return;

    observer.disconnect();
    window.clearTimeout(timer);
    mountHandle(asyncTarget, handleConfig);
  });

  timer = window.setTimeout(() => {
    observer.disconnect();
    console.warn(`[AI Handle] 等待超时，未找到挂载元素：${HANDLE_SELECTOR}`);
  }, timeout);

  observer.observe(document.documentElement, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialize(config), { once: true });
} else {
  initialize(config);
}
